import { useCallback, useEffect, useRef } from 'react';

export const debounce = <F extends (...args: any) => any>(func: F, waitFor: number) => {
  let timeout = 0;

  const debounced = (...args: any) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), waitFor);
  };

  return debounced as (...args: Parameters<F>) => ReturnType<F>;
};

export const debounceInComponent = <F extends (...args: any) => any>(func: F, waitFor: number) => {
  return useCallback(debounce(func, waitFor), []);
};

export const formatDate = (date: string) => {
  if (!date) return '';
  // The backend might already provide Z or UTC suffix. We normalize it.
  const normalizedDate = date.endsWith('Z') ? date : date + 'Z';
  const d = new Date(normalizedDate);
  if (isNaN(d.getTime())) return date; // Fallback to raw string if parsing fails

  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  });
};

export function calculateExecutionTime(startDateTimeString: string, endDateTimeString: string) {
  const startDate: Date = new Date(startDateTimeString);
  const endDate: Date = new Date(endDateTimeString);
  const timeInSeconds = (endDate.getTime() - startDate.getTime()) / 1000;

  const hours = Math.floor(timeInSeconds / 3600);
  const minutes = Math.floor((timeInSeconds % 3600) / 60);
  const seconds = Math.floor(timeInSeconds % 60);

  return hours >= 1 ? `${hours} h ${minutes} min` : `${minutes} min ${seconds} sec`;
}

export const DEV_MODE = import.meta.env.VITE_DEV_MODE == 'true';

export function useInterval(callback: () => any, delay: number) {
  const savedCallback = useRef<() => any>();

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    function tick() {
      if (savedCallback.current) savedCallback.current();
    }
    if (delay !== null) {
      const id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

export function useScrollListener(element: any, handleScroll: (e: any) => any, throttle = 1000) {
  const scrollingTimer = useRef<number>();

  const listenToScroll = useCallback(
    (e: any) => {
      clearTimeout(scrollingTimer.current);
      scrollingTimer.current = setTimeout(
        () =>
          requestAnimationFrame(() => {
            handleScroll(e);
          }),
        throttle, // we have a new parameter for the hook. handleScroll will fire max every throttle (ms)
      );
    },
    [scrollingTimer, throttle],
  );

  const removeScrollListener = useCallback(() => {
    if (element.current) {
      clearTimeout(scrollingTimer.current);
      element.current?.removeEventListener('scroll', listenToScroll);
    }
  }, [scrollingTimer, listenToScroll, element]);

  useEffect(() => {
    if (element.current) {
      element.current.addEventListener('scroll', listenToScroll);
    }
    return () => {
      removeScrollListener();
    };
  }, [listenToScroll, removeScrollListener, element]);
}

export function isObject(inputElement: any) {
  return typeof inputElement === 'object' && !Array.isArray(inputElement) && inputElement !== null ? false : true;
}

export const calculateCardWidth = (
  refCardsGrid: React.RefObject<HTMLDivElement> | null,
  cardStyle: string,
  minWidth: number,
  maxWidth: number,
  setCardWidth: any,
) => {
  if (refCardsGrid === null) return;
  const cardsGrid = refCardsGrid.current;
  if (!cardsGrid) return;
  const cardsGridWidth = parseFloat(window.getComputedStyle(cardsGrid).width);
  // We need to fetch the margin for aa card
  const acStyle = document.querySelector(`.${cardStyle}`);
  if (acStyle === null) return;
  const acMargin = parseFloat(window.getComputedStyle(acStyle!).margin);
  const acPadding = parseFloat(window.getComputedStyle(acStyle!).padding);

  // Define min and max width for a card + add the margin in calculations
  const minwidth = minWidth;
  const maxwidth = maxWidth;
  const numColumnsMinWidth = Math.floor(cardsGridWidth / (minwidth + acMargin * 2));
  const numColumnsMaxWidth = Math.floor(cardsGridWidth / (maxwidth + acMargin * 2));

  // Compare the number of columns for min and max width and set the width accordingly to create the best fit for the screen
  if (numColumnsMinWidth > numColumnsMaxWidth) {
    setCardWidth(cardsGridWidth / numColumnsMaxWidth - (acMargin * 2 + acPadding * 2));
  } else {
    setCardWidth(cardsGridWidth / numColumnsMinWidth - (acMargin * 2 + acPadding * 2));
  }
};

export function getSimpleSeededRandomGenerator(seed: number) {
  // NOTE: very basic -- not to be used for statistics!
  let theSeed = seed;

  return function () {
    theSeed = (theSeed * 9301 + 49297) % 233280;
    return theSeed / 233280;
  };
}

export function getUniqueValues(theList: any[], sortByFrequency?: boolean): any[] {
  // Returns the distinct elements in the list, optionally sorted by decreasing occurrence frequency
  const lookupMap: any = {};
  for (const elem of theList) {
    if (!(elem in lookupMap)) lookupMap[elem] = 0;
    lookupMap[elem] += 1;
  }
  if (!sortByFrequency) {
    const outputList: any[] = [];
    for (const elem in lookupMap) outputList.push(elem);
    return outputList;
  } else {
    const sortList: any[] = [];
    for (const elem in lookupMap) sortList.push({ elem, frequency: lookupMap[elem] });
    sortList.sort((elem1, elem2) => elem2.frequency - elem1.frequency);
    return sortList.map((elem) => elem.elem);
  }
}

export function getUserError(message: any): any {
  throw {
    userError: true,
    message: String(message),
    toString: function () {
      // to allow String(error) in the code
      return String(message);
    },
  };
}

export function parseFloatIfPossible(input: string) {
  const tryNumber = parseFloat(input);
  if (!isNaN(tryNumber)) return tryNumber;
  else return input;
}

export function isModifiableTemplateScope(scopeValue: string) {
  return scopeValue !== 'ontologies' && scopeValue !== 'startPageViews' && scopeValue !== 'uploadTemplates';
}
